import { useSyncExternalStore, useCallback } from 'react'

/**
 * Hook to check if a media query matches
 * Per Frontend-Architecture.md hooks pattern
 *
 * Uses useSyncExternalStore for proper React 18+ compatibility
 * and to avoid the setState-in-effect anti-pattern.
 *
 * @param query - Media query string (e.g., '(min-width: 1024px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (callback: () => void) => {
      if (typeof window === 'undefined') return () => {}
      const mediaQuery = window.matchMedia(query)
      mediaQuery.addEventListener('change', callback)
      return () => mediaQuery.removeEventListener('change', callback)
    },
    [query]
  )

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(query).matches
  }, [query])

  const getServerSnapshot = useCallback(() => false, [])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * Convenience hooks for common breakpoints
 * Per Design-System.md Section 4.4 breakpoints
 */

// Mobile: < 768px
export function useIsMobile(): boolean {
  return !useMediaQuery('(min-width: 768px)')
}

// Tablet: 768px - 1023px
export function useIsTablet(): boolean {
  const isAboveMobile = useMediaQuery('(min-width: 768px)')
  const isBelowDesktop = !useMediaQuery('(min-width: 1024px)')
  return isAboveMobile && isBelowDesktop
}

// Desktop: >= 1024px
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)')
}

// Large Desktop: >= 1280px
export function useIsLargeDesktop(): boolean {
  return useMediaQuery('(min-width: 1280px)')
}

// Compact Mode: < 1300px (triggers compact UI)
export function useIsCompactMode(): boolean {
  return !useMediaQuery('(min-width: 1300px)')
}
